import { useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'

interface UseStaleRefreshOptions {
  /**
   * Time in milliseconds after which data is considered stale.
   * Default: 5 minutes (300000ms)
   */
  staleThreshold?: number
  /**
   * Whether the hook is enabled. Can be used to disable refresh conditionally.
   * Default: true
   */
  enabled?: boolean
}

/**
 * Hook that automatically refreshes page data when the tab becomes visible
 * after being hidden for longer than the stale threshold.
 *
 * Uses the Page Visibility API to detect when the user returns to the tab.
 *
 * @example
 * // Refresh if tab was hidden for more than 5 minutes
 * useStaleRefresh()
 *
 * @example
 * // Refresh if tab was hidden for more than 2 minutes
 * useStaleRefresh({ staleThreshold: 2 * 60 * 1000 })
 */
export function useStaleRefresh({
  staleThreshold = 5 * 60 * 1000, // 5 minutes
  enabled = true
}: UseStaleRefreshOptions = {}) {
  const hiddenAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) return

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is now hidden - record the time
        hiddenAtRef.current = Date.now()
      } else {
        // Tab is now visible - check if we should refresh
        if (hiddenAtRef.current !== null) {
          const hiddenDuration = Date.now() - hiddenAtRef.current
          hiddenAtRef.current = null

          if (hiddenDuration >= staleThreshold) {
            // Data is stale, refresh the page
            router.reload()
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [staleThreshold, enabled])
}
